/**
 * Which WBS hierarchy Budget Management cost-plans against: the Cost Management
 * WBS by default, or the Execution / Financial WBS. Stored on
 * `projects.budget_wbs_source` (migration 00584); an instance without the
 * column, or a project with no value, plans against the Cost Management WBS.
 */
import { supabase } from '@/lib/supabase'
import {
  getWbsHierarchies,
  getSchedulingNodes,
  getCostNodes,
  getFinancialNodes,
} from '@/services/hierarchyService'
import { isBudgetWbsSource, type BudgetWbsSource, type PlanNode } from '@/lib/budgetWbsPlan'
import type { BaseHierarchyNode } from '@/types/hierarchy'

function isSupabaseMode(): boolean {
  return (import.meta.env.VITE_DATA_SOURCE as string) === 'supabase'
}

const LOCAL_KEY = (projectId: string) => `ppm-budget-wbs-source:${projectId}`

/** Placeholder roots the hierarchy service seeds when a hierarchy has no real nodes. */
const PLACEHOLDER_ROOT_PREFIXES = ['sn_root_', 'cn_root_', 'fn_root_']

export async function getBudgetWbsSource(projectId: string): Promise<BudgetWbsSource> {
  if (!projectId) return 'cost'
  if (isSupabaseMode()) {
    const { data, error } = await supabase
      .from('projects')
      .select('budget_wbs_source')
      .eq('id', projectId)
      .maybeSingle()
    if (error) {
      console.warn('[budgetWbsSource] read failed, using Cost Management WBS:', error.message)
      return 'cost'
    }
    const v = (data as { budget_wbs_source?: unknown } | null)?.budget_wbs_source
    return isBudgetWbsSource(v) ? v : 'cost'
  }
  try {
    const v = localStorage.getItem(LOCAL_KEY(projectId))
    return isBudgetWbsSource(v) ? v : 'cost'
  } catch {
    return 'cost'
  }
}

export async function setBudgetWbsSource(projectId: string, source: BudgetWbsSource): Promise<void> {
  if (isSupabaseMode()) {
    const { error } = await supabase.from('projects').update({ budget_wbs_source: source }).eq('id', projectId)
    if (error) throw new Error(`Could not save the budget WBS: ${error.message}`)
    return
  }
  try { localStorage.setItem(LOCAL_KEY(projectId), source) } catch { /* demo mode only */ }
}

type TypedNode = BaseHierarchyNode & { elementType?: string; nodeType?: string }

/** The chosen hierarchy's real nodes as plan nodes (empty when it has none). */
export async function getBudgetPlanNodes(projectId: string, source: BudgetWbsSource): Promise<PlanNode[]> {
  const hierarchies = await getWbsHierarchies(projectId)
  const h = hierarchies.find((x) => x.type === source && x.isDefault) ?? hierarchies.find((x) => x.type === source)
  let raw: TypedNode[] = []
  if (source === 'scheduling') raw = await getSchedulingNodes(h?.id ?? `hier_sched_${projectId}`, projectId)
  else if (h) raw = source === 'cost' ? await getCostNodes(h.id) : await getFinancialNodes(h.id)
  return raw
    .filter((n) => !PLACEHOLDER_ROOT_PREFIXES.some((p) => n.id.startsWith(p)))
    .map((n) => ({
      id: n.id,
      parentId: n.parentNodeId,
      code: n.code,
      name: n.name,
      level: n.level,
      sortOrder: n.sortOrder,
      type: n.elementType ?? n.nodeType ?? 'WbsElement',
    }))
}
